/* ============================================================================
 *  Which patients a medicine may be prescribed to, said out loud
 * ----------------------------------------------------------------------------
 *  store_pro.age_cat marks each product Adult, Child or Both, and
 *  /loadMedications filters the prescribing list by the patient's band. That
 *  filter was invisible in every direction:
 *
 *    - the list showed no band, so a clinician could not tell what a drug was
 *      restricted to. ("I don't know whether it is adult or child or both.")
 *    - the endpoint dropped age_cat from its response entirely, so the screen
 *      could not have shown it even if it wanted to.
 *    - nothing said a filter had been applied, so a drug missing because of its
 *      band looked exactly like a drug the facility does not stock.
 *    - and there was no way to lift it. The endpoint has always accepted
 *      ageCategory=all for precisely this — a child who genuinely needs an
 *      adult-only product must never be un-prescribable — but no screen ever
 *      sent it, so the escape hatch was unreachable.
 *
 *  The last one is the clinical risk. A prescribing list that silently omits
 *  drugs teaches the prescriber to distrust the search box, and the failure mode
 *  is a drug not given.
 *
 *  So: the band is shown on every row, the gate states itself, and lifting it is
 *  one click that is always available.
 * ========================================================================== */

import React from 'react';
import { Box, Chip, Button, Typography } from '@mui/material';

//  Muted for 'Both' — it is the overwhelming majority and the unremarkable case,
//  so it should read as background. The restrictions are what the eye needs.
const BAND_STYLE = {
  Adult: { bg: '#EDE9FE', fg: '#5B21B6', label: 'Adult' },
  Child: { bg: '#DBEAFE', fg: '#1E40AF', label: 'Child' },
  Both:  { bg: '#F1F5F9', fg: '#64748B', label: 'Both'  },
};

const normalise = (raw) => {
  const v = String(raw == null ? '' : raw).trim().toLowerCase();
  if (v === 'adult') return 'Adult';
  if (v === 'child') return 'Child';
  //  Anything unrecognised — blank, a legacy code, a value from an install that
  //  never ran migration 030 — reads as Both, which is what the SQL does too.
  return 'Both';
};

export const AgeBandChip = ({ value }) => {
  const band = normalise(value);
  const s = BAND_STYLE[band];
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 18, fontSize: '0.65rem', fontWeight: 700,
        backgroundColor: s.bg, color: s.fg,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
};

/*  The banner above the list.
 *
 *  Renders nothing when there is nothing to say — no age filter was applied, or
 *  it hid nothing. A permanent notice that never changes is one people stop
 *  reading, and this one matters on the day it does change.
 *
 *  `meta` is the /loadMedications response:
 *  { ageCategory, hiddenByAge, adultFromAge, ageLimit }
 *
 *  ── adultFromAge, NOT ageLimit ──────────────────────────────────────────────
 *
 *  A child is UNDER 12; at 12 the patient is an adult. `adultFromAge` says that
 *  directly ("12 and over is an adult") and is the field to read.
 *
 *  `ageLimit` is the older field and means the opposite thing — the oldest age
 *  that is still a child — so the server sends it as adultFromAge - 1. It is
 *  kept only so a browser running a stale bundle still prints something true,
 *  and it is the fallback here for the mirror-image case: a NEW bundle talking
 *  to a server that has not been restarted yet, which sends ageLimit and no
 *  adultFromAge. Reading ageLimit + 1 there keeps the sentence honest instead
 *  of printing "undefined".
 */
export const AgeBandNotice = ({ meta, showAll, onToggle }) => {
  const hidden = Number(meta?.hiddenByAge || 0);
  const band = meta?.ageCategory;
  //  See the note above: adultFromAge is authoritative, ageLimit + 1 is the
  //  bridge for a server that has not restarted yet, 12 is the last resort so
  //  the sentence can never read "under undefined years".
  const adultFrom = Number(meta?.adultFromAge)
    || (Number(meta?.ageLimit) ? Number(meta.ageLimit) + 1 : 12);

  if (showAll) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        px: 1, py: 0.5, mb: 0.5, borderRadius: 1,
        backgroundColor: '#FEF3C7', border: '1px solid #FDE68A',
      }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#92400E', flex: 1 }}>
          Showing <b>every age band</b>, including medicines not normally
          prescribed to this patient. Check the band on each row.
        </Typography>
        <Button size="small" onClick={onToggle}
          sx={{ fontSize: '0.68rem', py: 0, minWidth: 0 }}>
          Back to {band === 'Child' ? 'child' : 'adult'} list
        </Button>
      </Box>
    );
  }

  if (!band || hidden <= 0) return null;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      px: 1, py: 0.5, mb: 0.5, borderRadius: 1,
      backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0',
    }}>
      <Typography sx={{ fontSize: '0.72rem', color: '#475569', flex: 1 }}>
        This patient is <b>{band === 'Child'
          ? `a child (under ${adultFrom} years)`
          : `an adult (${adultFrom} years and over)`}</b>.
        {' '}{hidden} medicine{hidden === 1 ? '' : 's'} marked
        {' '}{band === 'Child' ? 'adult' : 'child'}-only {hidden === 1 ? 'is' : 'are'} hidden.
      </Typography>
      <Button size="small" onClick={onToggle}
        sx={{ fontSize: '0.68rem', py: 0, minWidth: 0 }}>
        Show all
      </Button>
    </Box>
  );
};

export default AgeBandChip;
